/* Zone detail: verification, then records.
 *
 * The verification panel leads, because an unverified zone is not served and
 * every other control on this page is therefore inert. The instruction shown
 * is the TXT challenge specifically -- see app/dns/verification.py: the TXT
 * token proves WHO holds the zone, whereas a delegation only proves that
 * SOMEBODY pointed a name at us. Do not present them as alternatives here.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useParams } from "react-router-dom";

import { api, type DnsZone } from "../api";
import { DynamicHosts } from "../components/DynamicHosts";
import { RecordEditor } from "../components/RecordEditor";

function VerificationPanel({ zone }: { zone: DnsZone }) {
  const queryClient = useQueryClient();

  const verify = useMutation({
    mutationFn: () => api.post<DnsZone>(`/api/dns/zones/${zone.id}/verify`),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ["dns", "zone", zone.id] }),
  });

  if (zone.verification_state === "verified") {
    return (
      <p className="ok">
        Verified{zone.verified_at ? ` on ${new Date(zone.verified_at).toLocaleDateString()}` : ""}.
      </p>
    );
  }

  return (
    <div className="panel warn">
      <h2>This zone is not verified, and is not being served</h2>
      <p>
        Publish this TXT record at your current DNS provider, then click Verify. It proves
        you control <code>{zone.name}</code>.
      </p>
      <dl>
        <dt>Name</dt>
        <dd>
          <code>{zone.verification_record}</code>
        </dd>
        <dt>Type</dt>
        <dd>
          <code>TXT</code>
        </dd>
        <dt>Value</dt>
        <dd>
          <code>{zone.verification_token}</code>
        </dd>
      </dl>
      <button onClick={() => verify.mutate()} disabled={verify.isPending}>
        {verify.isPending ? "Checking…" : "Verify"}
      </button>
      {verify.isError && <p className="bad">{(verify.error as Error).message}</p>}
      {verify.isSuccess && verify.data.verification_state !== "verified" && (
        <p className="warn">
          Not found yet. DNS changes can take a few minutes to propagate — try again shortly.
        </p>
      )}
    </div>
  );
}

export function DnsZoneDetail() {
  const { zoneId = "" } = useParams();

  const zone = useQuery({
    queryKey: ["dns", "zone", zoneId],
    queryFn: () => api.get<DnsZone>(`/api/dns/zones/${zoneId}`),
    enabled: Boolean(zoneId),
  });

  if (zone.isLoading) return <p className="muted">Loading…</p>;
  if (zone.isError) return <p className="bad">{(zone.error as Error).message}</p>;
  if (!zone.data) return null;

  return (
    <section>
      <p className="label">
        <Link to="/dns">← All zones</Link>
      </p>
      <h1>{zone.data.name}</h1>
      <VerificationPanel zone={zone.data} />
      {/* An unverified zone is not served, so editing is offered only once
          verified -- the backend enforces this independently with a 409
          naming the TXT record to publish; this gate exists so the client
          never reaches it, not because the backend needs help. */}
      {zone.data.verification_state === "verified" && <RecordEditor zoneId={zone.data.id} />}
      {zone.data.verification_state === "verified" && <DynamicHosts zoneId={zone.data.id} />}
    </section>
  );
}
